import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Bell, Loader2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { FOCUS_AREAS } from "@/lib/focusAreas";
import { Badge } from "@/components/ui/badge";
import { useIsPro } from "@/hooks/useSubscription";
import ProGate from "@/components/payments/ProGate";

type Prefs = {
  user_id: string;
  cadence: "instant" | "daily" | "off";
  email_enabled: boolean;
};

export default function CoachAlertPrefs() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isPro = useIsPro();

  const { data: prefs } = useQuery({
    enabled: !!user,
    queryKey: ["coach-alert-prefs", user?.id],
    queryFn: async (): Promise<Prefs> => {
      const { data } = await supabase
        .from("coach_alert_prefs")
        .select("user_id, cadence, email_enabled")
        .eq("user_id", user!.id)
        .maybeSingle();
      return (data as Prefs) ?? { user_id: user!.id, cadence: "daily", email_enabled: false };
    },
  });

  const { data: follows } = useQuery({
    enabled: !!user,
    queryKey: ["coach-follows", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("coach_follows")
        .select("target_type, target_key")
        .eq("user_id", user!.id)
        .eq("target_type", "focus_area");
      return data ?? [];
    },
  });

  const update = useMutation({
    mutationFn: async (patch: Partial<Prefs>) => {
      const { error } = await supabase
        .from("coach_alert_prefs")
        .upsert({ user_id: user!.id, ...prefs, ...patch }, { onConflict: "user_id" });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["coach-alert-prefs", user?.id] }),
    onError: (e: any) => toast.error(e.message ?? "Could not save"),
  });

  const toggleFocus = useMutation({
    mutationFn: async (fa: string) => {
      const exists = (follows ?? []).some((f: any) => f.target_key === fa);
      if (exists) {
        const { error } = await supabase
          .from("coach_follows").delete()
          .eq("user_id", user!.id).eq("target_type", "focus_area").eq("target_key", fa);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("coach_follows")
          .insert({ user_id: user!.id, target_type: "focus_area", target_key: fa });
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["coach-follows", user?.id] }),
    onError: (e: any) => toast.error(e.message ?? "Could not update follows"),
  });

  if (!prefs) return null;
  const followed = new Set((follows ?? []).map((f: any) => f.target_key));

  if (!isPro) {
    return <ProGate feature="Custom alerts">{null}</ProGate>;
  }

  return (
    <section className="bg-card border border-border rounded-xl p-5 space-y-5">
      <div className="flex items-center gap-2">
        <Bell className="h-4 w-4 text-primary" />
        <h3 className="font-semibold text-sm uppercase tracking-wide">Alert preferences</h3>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">Cadence</Label>
        <div className="flex gap-2 mt-2">
          {(["instant", "daily", "off"] as const).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => update.mutate({ cadence: c })}
              className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${
                prefs.cadence === c
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {c === "instant" ? "Instant" : c === "daily" ? "Daily digest" : "Off"}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm">Email alerts</Label>
          <p className="text-xs text-muted-foreground">In-app notifications are always on.</p>
        </div>
        <Switch
          checked={prefs.email_enabled}
          onCheckedChange={(v) => update.mutate({ email_enabled: v })}
        />
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">Follow focus areas</Label>
        <p className="text-xs text-muted-foreground mt-1 mb-3">
          Alerts include any bill, insight, or project matching these areas.
        </p>
        <div className="flex flex-wrap gap-2">
          {FOCUS_AREAS.map((fa) => {
            const on = followed.has(fa);
            return (
              <button key={fa} type="button" onClick={() => toggleFocus.mutate(fa)} disabled={toggleFocus.isPending}>
                <Badge
                  variant={on ? "default" : "outline"}
                  className={`cursor-pointer ${on ? "" : "hover:border-primary/40"}`}
                >
                  {toggleFocus.isPending && toggleFocus.variables === fa ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : null}
                  {fa}
                </Badge>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}